import React, { useState } from 'react';
import { clinicService } from '../../services/api';

/**
 * Export / update the current clinic's full configuration via JSON.
 */
const ConfigJsonUpdatePanel = ({ clinicId, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const handleExport = async () => {
    try {
      setBusy(true);
      setError(null);
      setSuccess(null);
      const data = await clinicService.exportClinicConfiguration(clinicId);
      const text = JSON.stringify(data, null, 2);
      setJsonText(text);

      const blob = new Blob([text], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clinic_${clinicId}_config.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setSuccess('Exported clinic configuration JSON.');
      console.log('[ConfigJsonUpdatePanel] export ok', data);
    } catch (err) {
      console.error('[ConfigJsonUpdatePanel] export failed', err);
      setError(err.response?.data?.detail || err.message || 'Failed to export configuration');
    } finally {
      setBusy(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        setJsonText(JSON.stringify(parsed, null, 2));
        setError(null);
        setSuccess(`Loaded ${file.name}`);
      } catch (err) {
        setError('Invalid JSON file.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleUpdate = async () => {
    try {
      setBusy(true);
      setError(null);
      setSuccess(null);
      setLastResult(null);

      const parsed = JSON.parse(jsonText);
      if (!parsed.clinic_configuration) {
        setError('JSON must include clinic_configuration.');
        return;
      }

      const collections = Object.keys(parsed).filter((k) => k.startsWith('clinic_') && k !== 'clinic_configuration');
      if (
        !window.confirm(
          `Update clinic ${clinicId} from JSON?\n\n` +
            `Clinic fields will be updated.\n` +
            (collections.length
              ? `These collections will be REPLACED: ${collections.join(', ')}`
              : 'No child collections in JSON — only clinic fields will change.')
        )
      ) {
        return;
      }

      const response = await clinicService.updateClinicConfigurationFromJson(clinicId, parsed);
      setLastResult(response);
      setSuccess(response.message || 'Clinic configuration updated.');
      console.log('[ConfigJsonUpdatePanel] update ok', response);
      if (onUpdated) onUpdated(response);
    } catch (err) {
      console.error('[ConfigJsonUpdatePanel] update failed', err);
      setError(err.response?.data?.detail || err.message || 'Failed to update configuration');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6 border rounded-lg bg-white shadow">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div>
          <p className="font-medium text-gray-800">Update all configs via JSON</p>
          <p className="text-sm text-gray-500">
            Export the full clinic config, edit JSON, then apply updates to this clinic in place.
          </p>
        </div>
        <span className="text-sm text-blue-600">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t">
          <div className="flex flex-wrap gap-2 pt-3">
            <button
              type="button"
              onClick={handleExport}
              disabled={busy}
              className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            >
              Export JSON
            </button>
            <label className="px-3 py-1.5 text-sm bg-white border rounded hover:bg-gray-50 cursor-pointer">
              Upload JSON
              <input type="file" accept="application/json,.json" className="hidden" onChange={handleFileUpload} />
            </label>
            <button
              type="button"
              onClick={handleUpdate}
              disabled={busy || !jsonText.trim()}
              className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
            >
              {busy ? 'Working...' : 'Update this clinic'}
            </button>
          </div>

          <p className="text-xs text-gray-500">
            Collections present in the JSON (e.g. <code>clinic_doctors</code>, <code>clinic_doctor_schedules</code>,{' '}
            <code>clinic_qa_knowledge_base</code>) replace existing rows for this clinic. Omitted collections are left unchanged.
            To create a brand-new clinic from JSON, use Clinics → Import instead.
          </p>

          <textarea
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            placeholder='{"clinic_configuration": {...}, "clinic_doctors": [...], "clinic_doctor_schedules": [...]}'
            rows={18}
            className="w-full font-mono text-xs p-3 border rounded bg-gray-50"
          />

          {error && <div className="p-3 bg-red-100 text-red-700 rounded text-sm">{error}</div>}
          {success && <div className="p-3 bg-green-100 text-green-700 rounded text-sm">{success}</div>}
          {lastResult && (
            <pre className="p-3 bg-gray-50 border rounded text-xs overflow-auto max-h-48">
              {JSON.stringify(lastResult, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export default ConfigJsonUpdatePanel;
